import React, { useRef, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  copyToClipboard,
  copyToClipboardSync,
} from "../../utils/copyToClipboard";
import "./FormSubmissionSuccessDialog.scss";

const FormSubmissionSuccessDialog = ({
  open,
  onClose,
  title,
  eyebrow = "Request submitted",
  message,
  trackingId,
  trackingLabel = "Tracking ID",
  children,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyInProgressRef = useRef(false);

  const runCopy = async () => {
    if (!trackingId || copyInProgressRef.current) return;

    copyInProgressRef.current = true;
    const value = String(trackingId);
    let copiedSuccessfully = copyToClipboardSync(value);

    if (!copiedSuccessfully) {
      copiedSuccessfully = await copyToClipboard(value);
    }

    if (copiedSuccessfully) {
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCopied(false);
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 3000);
    }

    copyInProgressRef.current = false;
  };

  const handleCopyTrigger = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void runCopy();
  };

  const copyTooltipTitle = copied
    ? "Copied!"
    : copyFailed
      ? "Copy failed — select ID and press Ctrl+C"
      : "Copy";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="form-submit-success-dialog"
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
      PaperProps={{ className: "form-submit-success-dialog__paper" }}
    >
      <DialogContent className="form-submit-success-dialog__content">
        <div className="form-submit-success-dialog__icon-wrap">
          <DoneIcon className="form-submit-success-dialog__icon" />
        </div>
        <p className="form-submit-success-dialog__eyebrow">{eyebrow}</p>
        <Typography component="h2" className="form-submit-success-dialog__title">
          {title}
        </Typography>
        {message ? (
          <p className="form-submit-success-dialog__message">{message}</p>
        ) : null}
        {trackingId ? (
          <div className="form-submit-success-dialog__id-box">
            <div className="form-submit-success-dialog__id-text">
              <span className="form-submit-success-dialog__id-label">
                {trackingLabel}
              </span>
              <p
                className="form-submit-success-dialog__id-value"
                onMouseDown={handleCopyTrigger}
                onClick={handleCopyTrigger}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleCopyTrigger(event);
                  }
                }}
                role="button"
                tabIndex={0}
                title="Click to copy"
              >
                {trackingId}
              </p>
            </div>
            <Tooltip title={copyTooltipTitle}>
              <IconButton
                type="button"
                className="form-submit-success-dialog__copy-btn"
                onMouseDown={handleCopyTrigger}
                onClick={handleCopyTrigger}
                aria-label="Copy tracking ID"
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
        {children ? (
          <div className="form-submit-success-dialog__extra">{children}</div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default FormSubmissionSuccessDialog;